import { useEffect, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import { Anchor } from '@klv/atoms/anchor';
import { generateClassName, windowIsDefined } from '@klv/shared/utils/functions';

import * as style from './styles/pagination.module.scss';

import type { IPagination } from './types';

const maxItemsPerWindowWidth = [
  {
    minWidth: 768,
    maxItems: 9,
  },
  {
    minWidth: 576,
    maxItems: 7,
  },
  {
    minWidth: 0,
    maxItems: 5,
  },
];

function getMaxItems(windowWidth: number) {
  const widthObj = maxItemsPerWindowWidth.find((maxItemObj) => windowWidth >= maxItemObj.minWidth) || maxItemsPerWindowWidth[2];

  return widthObj.maxItems;
}

function createPageItem(baseUrl: string | null, pageIndex: number, currentPage: number) {
  return (
    <li
      className={generateClassName([style.paginationItem, pageIndex === currentPage ? style.currentPage : ''])}
      key={pageIndex}
    >
      {baseUrl ? (
        <Anchor to={`${baseUrl}/${pageIndex ? (pageIndex + 1) : ''}`}>
          { pageIndex + 1 }
        </Anchor>
      ) : (
        '...'
      )}
    </li>
  );
}

function createPaginationList(totalPages: number, currentPage: number, baseUrl: string, windowWidth: number) {
  const maxItems = getMaxItems(windowWidth);
  const useEllipses = (totalPages > maxItems);

  const items = Array.from({ length: (useEllipses) ? 5 : totalPages });

  if (!useEllipses) {
    return items.map((x, i) => createPageItem(baseUrl, i, currentPage));
  }

  const hasLowerEllipsis = currentPage > maxItems / 2;
  const hasUpperEllipsis = currentPage < (totalPages - 1 - (maxItems / 2));

  // Create first two and last two items in pagination
  items[0] = createPageItem(baseUrl, 0, currentPage);
  items[items.length - 1] = createPageItem(baseUrl, totalPages - 1, currentPage);

  items[1] = createPageItem((hasLowerEllipsis) ? null : baseUrl, 1, currentPage);
  items[items.length - 2] = createPageItem((hasUpperEllipsis) ? null : baseUrl, totalPages - 2, currentPage);

  // Create middle items in own array, then flatten together
  const shoulderLength = Math.floor(maxItems / 2) - 2; // 0 with small screens, up to 2
  const lowerEllipsisCutoff = 3 + shoulderLength;
  const upperEllipsisCutoff = totalPages - (3 + shoulderLength);

  let midItems = [];
  const midBaseArray = Array.from({ length: (shoulderLength * 2) + 1 });

  if (!hasLowerEllipsis && (currentPage < lowerEllipsisCutoff)) {
    midItems = midBaseArray.map((nullItem, i) => createPageItem(baseUrl, (2 + i), currentPage));
  } else if (!hasUpperEllipsis && (currentPage >= upperEllipsisCutoff)) {
    midItems = midBaseArray.map((nullItem, i) => createPageItem(baseUrl, ((upperEllipsisCutoff - shoulderLength) + i), currentPage));
  } else {
    const midStartingPage = currentPage - shoulderLength;
    midItems = midBaseArray.map((nullItem, i) => createPageItem(baseUrl, (midStartingPage + i), currentPage));
  }

  items[2] = midItems;
  return items.flat();
}

const Pagination = ({
  className, numberOfPages, currentPage, baseUrl, previousPagePath, nextPagePath, ...props
}: IPagination) => {
  const [windowWidth, setWindowWidth] = useState(0);
  const editedBaseUrl = baseUrl.replace('klaviyo-com', '');
  const { t } = useTranslation();

  function onWindowResize() {
    if (windowIsDefined()) {
      setWindowWidth(window.innerWidth);
    }
  }

  useEffect(() => {
    onWindowResize();
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  return (
    <div className={generateClassName([style.paginationWrapper, 'p2 semibold', className])} {...props}>
      <nav>
        <Anchor
          className={generateClassName([style.paginationSideButton, !previousPagePath ? style.disabled : ''])}
          to={previousPagePath}
        >
          {t('molecules_pagination_previous')}
        </Anchor>

        <ul className={style.paginationItems}>
          {/* @ts-ignore: worked in react 17 */}
          {createPaginationList(numberOfPages, currentPage, editedBaseUrl, windowWidth)}
        </ul>

        <Anchor
          className={generateClassName([style.paginationSideButton, !nextPagePath ? style.disabled : ''])}
          to={nextPagePath}
        >
          {t('molecules_pagination_next')}
        </Anchor>
      </nav>
    </div>
  );
};

export default Pagination;
